<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { workflowFieldsToFormFields } from './workflowFieldsToFormFields';
import {
  stripWorkflowFields,
  mergeFormFields,
} from './mergeFormFields';
import TicketFormsAPI from 'dashboard/api/ticketForms';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import CustomAttributeBundlesAPI from 'dashboard/api/customAttributeBundles';
import { defaultFormCountry } from './accountFormDefaults';
import FormList from './FormList.vue';
import PageEditor from './PageEditor.vue';
import FieldPalette from './FieldPalette.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const store = useStore();

const { t } = useI18n();

// Ticket custom-attribute definitions (labels, display types, list options)
// used to translate a subcategory's workflow fields into form fields.
const ticketAttributes = useMapGetter('attributes/getTicketAttributes');

const forms = ref([]);
const selectedFormId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);

// New form modal state
const showNewFormModal = ref(false);
const newFormName = ref('');
const newFormType = ref('ticket');
const newFormCategoryId = ref('');
const ticketCategories = ref([]);

// Subcategories for the current form's category
const subcategories = ref([]);
const loadingSubcats = ref(false);

async function fetchTicketCategories() {
  try {
    const { data } = await TicketCategoriesAPI.get();
    ticketCategories.value = Array.isArray(data) ? data : [];
  } catch {
    ticketCategories.value = [];
  }
}

// Local editable copy of the selected form
const editableForm = ref(null);

async function loadSubcategories(categoryId) {
  subcategories.value = [];
  if (!categoryId) return;
  loadingSubcats.value = true;
  try {
    const res = await TicketCategoriesAPI.getSubcategories(categoryId);
    const raw = res.data?.payload ?? res.data;
    subcategories.value = Array.isArray(raw) ? raw : [];
  } catch {
    subcategories.value = [];
  } finally {
    loadingSubcats.value = false;
  }
}

// Stable id generator shared by pages and fields.
function genId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// Existing forms predate page ids; backfill so branching targets stay stable.
function backfillPageIds(form) {
  const formPages = form?.form_definition?.pages || form?.form_options?.pages;
  if (!Array.isArray(formPages)) return;
  formPages.forEach(page => {
    if (page && !page.id) page.id = genId('page');
  });
}

// Fetch single form with full detail
async function fetchFormDetail(formId) {
  if (!formId) return;
  try {
    const { data } = await TicketFormsAPI.show(formId);
    editableForm.value = structuredClone(data);
    backfillPageIds(editableForm.value);
    await loadSubcategories(data.ticket_category_id);
  } catch {
    editableForm.value = null;
  }
}

function selectForm(formId) {
  selectedFormId.value = formId;
  fetchFormDetail(formId);
}

// Fetch all forms for this account
async function fetchForms() {
  isLoading.value = true;
  try {
    const { data } = await TicketFormsAPI.get();
    const list = data?.payload ?? data;
    forms.value = Array.isArray(list) ? list : [];
    const routeFormId = route.params.formId
      ? Number(route.params.formId)
      : null;
    if (routeFormId && forms.value.some(f => f.id === routeFormId)) {
      selectForm(routeFormId);
    } else if (forms.value.length && !selectedFormId.value) {
      selectForm(forms.value[0].id);
    }
  } catch {
    forms.value = [];
  } finally {
    isLoading.value = false;
  }
}

// CRUD: Add new form — opens modal
function addForm() {
  newFormName.value = '';
  newFormType.value = 'ticket';
  newFormCategoryId.value = '';
  if (ticketCategories.value.length === 0) fetchTicketCategories();
  showNewFormModal.value = true;
}

function cancelNewForm() {
  showNewFormModal.value = false;
}

async function confirmNewForm() {
  const isTicket = newFormType.value === 'ticket';
  if (!newFormName.value.trim() || (isTicket && !newFormCategoryId.value))
    return;

  const ticketForm = {
    name: newFormName.value.trim(),
    form_type: newFormType.value,
    active: true,
    form_options: {
      pages: [
        {
          id: genId('page'),
          title: t('FORM_BUILDER.PAGE.DEFAULT_TITLE'),
          description: '',
          nav_label: '',
          fields: [],
        },
      ],
    },
  };
  if (isTicket) {
    ticketForm.ticket_category_id = newFormCategoryId.value;
  } else {
    ticketForm.confirmation_message = t(
      'FORM_BUILDER.NEW_FORM_MODAL.CONTACT_CONFIRMATION'
    );
  }
  const payload = { ticket_form: ticketForm };
  try {
    const { data } = await TicketFormsAPI.create(payload);
    forms.value.push(data);
    selectForm(data.id);
    showNewFormModal.value = false;
    useAlert(t('FORM_BUILDER.ALERTS.CREATED'));
  } catch {
    useAlert(t('FORM_BUILDER.ALERTS.CREATE_ERROR'));
  }
}

// Save current form
async function saveForm() {
  if (!editableForm.value) return;
  isSaving.value = true;
  try {
    const payload = {
      ticket_form: {
        name: editableForm.value.name,
        active: editableForm.value.active,
        form_type: editableForm.value.form_type,
        allow_contact_update: editableForm.value.allow_contact_update,
        creates_contact: editableForm.value.creates_contact,
        contact_primary_identifier:
          editableForm.value.contact_primary_identifier || null,
        ticket_category_id: editableForm.value.ticket_category_id,
        default_subcategory_id:
          editableForm.value.default_subcategory_id || null,
        description: editableForm.value.description,
        slug: editableForm.value.slug,
        confirmation_message: editableForm.value.confirmation_message,
        form_definition: editableForm.value.form_definition,
      },
    };
    const { data } = await TicketFormsAPI.update(
      editableForm.value.id,
      payload
    );
    // Update in list
    const idx = forms.value.findIndex(f => f.id === data.id);
    if (idx !== -1) forms.value[idx] = data;
    editableForm.value = structuredClone(data);
    useAlert(t('FORM_BUILDER.ALERTS.SAVED'));
  } catch {
    useAlert(t('FORM_BUILDER.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
}

// Delete current form
async function deleteForm() {
  if (!editableForm.value) return;
  try {
    await TicketFormsAPI.delete(editableForm.value.id);
    forms.value = forms.value.filter(f => f.id !== editableForm.value.id);
    editableForm.value = null;
    selectedFormId.value = null;
    if (forms.value.length) selectForm(forms.value[0].id);
    useAlert(t('FORM_BUILDER.ALERTS.DELETED'));
  } catch {
    useAlert(t('FORM_BUILDER.ALERTS.DELETE_ERROR'));
  }
}

// Toggle active
function toggleActive() {
  if (!editableForm.value) return;
  editableForm.value.active = !editableForm.value.active;
}

// Toggle whether submissions may update an existing contact
function toggleAllowContactUpdate() {
  if (!editableForm.value) return;
  editableForm.value.allow_contact_update =
    !editableForm.value.allow_contact_update;
}

// Toggle whether the form only creates/updates a contact (no ticket)
function toggleCreatesContact() {
  if (!editableForm.value) return;
  editableForm.value.creates_contact = !editableForm.value.creates_contact;
}

// Whether this form is contact-focused (exposes contact matching settings)
const isContactFocusedForm = computed(
  () =>
    editableForm.value?.form_type === 'contact' ||
    editableForm.value?.creates_contact
);

// Update the configured primary identifier used to match existing contacts
function updateContactPrimaryIdentifier(e) {
  if (!editableForm.value) return;
  editableForm.value.contact_primary_identifier = e.target.value || null;
}

// Update form name
function updateFormName(e) {
  if (!editableForm.value) return;
  editableForm.value.name = e.target.value;
}

// Update form description
function updateFormDescription(e) {
  if (!editableForm.value) return;
  editableForm.value.description = e.target.value;
}

// Update form slug
function updateFormSlug(e) {
  if (!editableForm.value) return;
  editableForm.value.slug = e.target.value;
}

// Update form confirmation message
function updateConfirmationMessage(e) {
  if (!editableForm.value) return;
  editableForm.value.confirmation_message = e.target.value;
}

// Toggle form header config
const showFormHeader = ref(false);

function toggleFormHeaderConfig() {
  showFormHeader.value = !showFormHeader.value;
}

function previewForm() {
  if (!editableForm.value?.identifier) return;
  window.open(`/forms/${editableForm.value.identifier}`, '_blank');
}

// Page operations
function ensureFormDefinition() {
  if (!editableForm.value.form_definition) {
    editableForm.value.form_definition = { pages: [] };
  }
  if (!editableForm.value.form_definition.pages) {
    editableForm.value.form_definition.pages = [];
  }
}

function addPage() {
  ensureFormDefinition();
  editableForm.value.form_definition.pages.push({
    id: genId('page'),
    title: t('FORM_BUILDER.PAGE.DEFAULT_TITLE'),
    description: '',
    nav_label: '',
    fields: [],
  });
}

function updatePage(pageIndex, updatedPage) {
  ensureFormDefinition();
  editableForm.value.form_definition.pages[pageIndex] = updatedPage;
}

function deletePage(pageIndex) {
  ensureFormDefinition();
  editableForm.value.form_definition.pages.splice(pageIndex, 1);
}

// Field operations
function updateField(pageIndex, fieldIndex, updatedField) {
  ensureFormDefinition();
  editableForm.value.form_definition.pages[pageIndex].fields[fieldIndex] =
    updatedField;
}

function deleteField(pageIndex, fieldIndex) {
  ensureFormDefinition();
  editableForm.value.form_definition.pages[pageIndex].fields.splice(
    fieldIndex,
    1
  );
}

function createFieldFromType(fieldType) {
  return {
    id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    field_type: fieldType,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(`FORM_BUILDER.PALETTE.${fieldType.toUpperCase()}`),
    placeholder: '',
    required: false,
    options: fieldType === 'select' ? ['Option 1', 'Option 2'] : [],
    mapped_attribute: '',
    validation_regex: '',
    max_files: fieldType === 'file' ? 5 : undefined,
    max_file_size: fieldType === 'file' ? 10 : undefined,
    documents: fieldType === 'documents' ? [] : undefined,
    include_subcategory_templates: fieldType === 'documents' ? true : undefined,
    // Bias new address fields to the account's default country (e.g. Venezuela for
    // the Reencuentro account) so search + map start in the right place.
    country:
      fieldType === 'address'
        ? defaultFormCountry(route.params.accountId)
        : undefined,
  };
}

function dropFieldOnPage(pageIndex, fieldType, fieldData) {
  ensureFormDefinition();
  const page = editableForm.value.form_definition.pages[pageIndex];
  if (!page.fields) page.fields = [];

  // A bundle (e.g. the address block) drops several pre-wired fields at once.
  // Preserve their ids so the search field's autofill targets stay valid.
  if (Array.isArray(fieldData)) {
    if (fieldData.some(isMappedAttributeTaken)) {
      useAlert(t('FORM_BUILDER.FIELD_ALREADY_ADDED'));
      return;
    }
    fieldData.forEach(field => {
      page.fields.push({
        ...field,
        id:
          field.id ||
          `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      });
    });
    return;
  }

  if (fieldData) {
    // Moving from another page, or palette preset (ticket fields) via field-data
    const { sourcePageIndex, sourceFieldIndex, ...field } = fieldData;
    const isMove =
      sourcePageIndex !== undefined && sourceFieldIndex !== undefined;
    // Block a palette preset (not a move) for a mapped field already on the form.
    if (!isMove && isMappedAttributeTaken(field)) {
      useAlert(t('FORM_BUILDER.FIELD_ALREADY_ADDED'));
      return;
    }
    if (isMove) {
      editableForm.value.form_definition.pages[sourcePageIndex].fields.splice(
        sourceFieldIndex,
        1
      );
    }
    page.fields.push({
      ...field,
      id:
        field.id ||
        `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    });
  } else if (fieldType) {
    page.fields.push(createFieldFromType(fieldType));
  }
}

// Add field from palette click (appends to last page)
function addFieldFromPalette(field) {
  // A specific ticket/contact field can be used at most once per form.
  if (isMappedAttributeTaken(field)) {
    useAlert(t('FORM_BUILDER.FIELD_ALREADY_ADDED'));
    return;
  }
  ensureFormDefinition();
  const pages = editableForm.value.form_definition.pages;
  if (pages.length === 0) addPage();
  const lastPage = pages[pages.length - 1];
  if (!lastPage.fields) lastPage.fields = [];
  lastPage.fields.push({
    ...field,
    id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  });
}

// Add a bundle (several pre-wired fields) from the palette. Preserves each
// field's id so cross-field references (autofill targets) survive. Blocks if any
// mapped field is already on the form.
function addFieldsFromPalette(fields) {
  const list = Array.isArray(fields) ? fields : [fields];
  if (list.some(isMappedAttributeTaken)) {
    useAlert(t('FORM_BUILDER.FIELD_ALREADY_ADDED'));
    return;
  }
  ensureFormDefinition();
  const pages = editableForm.value.form_definition.pages;
  if (pages.length === 0) addPage();
  const lastPage = pages[pages.length - 1];
  if (!lastPage.fields) lastPage.fields = [];
  list.forEach(field => {
    lastPage.fields.push({
      ...field,
      id:
        field.id ||
        `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    });
  });
}

// Give a field the same id shape used elsewhere in the builder.
function withFieldId(field) {
  return {
    ...field,
    id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  };
}

// Remove previously auto-added (source:'workflow') fields from every page.
function clearWorkflowFields() {
  const formPages = editableForm.value?.form_definition?.pages || [];
  formPages.forEach(page => {
    page.fields = stripWorkflowFields(page.fields);
  });
}

// Clear old workflow fields, then add the chosen subcategory's workflow fields
// to the first page. Only adds mappings not already on the form.
function applyWorkflowFields(subcategoryId) {
  clearWorkflowFields();
  if (!subcategoryId || editableForm.value?.form_type !== 'ticket') return;

  const sub = subcategories.value.find(s => s.id === subcategoryId);
  const workflowConfig = sub?.workflow_config;
  if (!Array.isArray(workflowConfig?.fields) || workflowConfig.fields.length === 0)
    return;

  ensureFormDefinition();
  const formPages = editableForm.value.form_definition.pages;
  if (formPages.length === 0) addPage();

  const translated = workflowFieldsToFormFields(
    workflowConfig,
    ticketAttributes.value
  );
  const existing = formPages.flatMap(page => page.fields || []);
  const toAdd = mergeFormFields(existing, translated).map(withFieldId);
  if (toAdd.length === 0) return;

  formPages[0].fields = [...(formPages[0].fields || []), ...toAdd];
  useAlert(t('FORM_BUILDER.WORKFLOW_FIELDS_ADDED', { count: toAdd.length }));
}

// Category changed: drop old workflow fields, reset subcategory, reload options.
async function onCategoryChange() {
  clearWorkflowFields();
  editableForm.value.default_subcategory_id = null;
  await loadSubcategories(editableForm.value.ticket_category_id);
}

// Subcategory changed: regenerate workflow fields for the new subcategory.
function onSubcategoryChange() {
  applyWorkflowFields(editableForm.value?.default_subcategory_id);
}

const pages = computed(() => editableForm.value?.form_definition?.pages || []);

// Flat list of fields (id + label) used to populate dependency pickers for
// conditional visibility/requirement rules. Group members are included.
const availableFields = computed(() =>
  pages.value
    .flatMap(p => p.fields || [])
    .flatMap(f =>
      f.field_type === 'group'
        ? (f.members || []).map(m => ({
            value: m.name || m.id,
            label: m.label || m.name || m.id,
          }))
        : [{ value: f.id, label: f.label || f.id }]
    )
    .filter(o => o.value)
);

// Non-empty mapped_attribute values already used anywhere on the form. Drives
// palette filtering and the duplicate guard so a specific ticket/contact field
// is used at most once (generic fields have no mapped_attribute, so they repeat).
const usedMappedAttributes = computed(() =>
  pages.value
    .flatMap(p => p.fields || [])
    .flatMap(f =>
      f.field_type === 'group'
        ? [`group:${f.catalog}`, ...(f.members || []).map(m => m.mapped_attribute)]
        : [f.mapped_attribute]
    )
    .filter(Boolean)
);

function isMappedAttributeTaken(field) {
  return (
    !!field.mapped_attribute &&
    usedMappedAttributes.value.includes(field.mapped_attribute)
  );
}

// Mapped attributes that let a submission resolve a contact
// (mirror of TicketForm::CONTACT_IDENTIFIER_MAPPINGS).
const CONTACT_IDENTIFIER_MAPPINGS = [
  'contact.email',
  'contact.phone_number',
  'contact.identifier',
  'ticket.contract_number',
];

// Check if the form can identify a contact (required for contact linking)
const hasContactIdentifier = computed(() => {
  const allFields = pages.value.flatMap(p => p.fields || []);
  return allFields.some(
    f =>
      ['email', 'phone'].includes(f.field_type) ||
      CONTACT_IDENTIFIER_MAPPINGS.includes(f.mapped_attribute)
  );
});

// Custom-attribute bundles for the current form's model — surfaced as one-click
// cards in the palette. Bundles are created in Settings → Custom attributes.
const bundles = ref([]);
async function fetchBundles() {
  const formType = editableForm.value?.form_type;
  if (!formType) {
    bundles.value = [];
    return;
  }
  const attributeModel =
    formType === 'contact' ? 'contact_attribute' : 'ticket_attribute';
  try {
    const { data } = await CustomAttributeBundlesAPI.get({
      attribute_model: attributeModel,
    });
    bundles.value = Array.isArray(data) ? data : [];
  } catch {
    bundles.value = [];
  }
}

watch(() => editableForm.value?.form_type, fetchBundles, { immediate: true });

onMounted(() => {
  fetchForms();
  // Populate the Category dropdown on load (not only when the New Form modal opens).
  fetchTicketCategories();
  // Load custom attributes so the palette + workflow prefill can use them.
  store.dispatch('attributes/get');
});

watch(
  () => route.params.formId,
  newId => {
    if (newId) selectForm(Number(newId));
  }
);
</script>

<template>
  <div class="flex w-full h-full overflow-hidden">
    <!-- Left: Form List -->
    <FormList
      :forms="forms"
      :selected-form-id="selectedFormId"
      @select="selectForm"
      @add="addForm"
    />

    <!-- Center + Right -->
    <template v-if="editableForm">
      <!-- Center: Form Builder -->
      <div class="flex-1 flex flex-col overflow-hidden">
        <!-- Toolbar -->
        <div
          class="px-4 py-2.5 border-b border-n-weak flex items-center gap-3 bg-n-background"
        >
          <div class="flex-1 flex items-center gap-3 min-w-0">
            <input
              type="text"
              :value="editableForm.name"
              class="text-base font-medium text-n-slate-12 bg-transparent border-none px-0 focus:outline-none focus:ring-0 min-w-0 max-w-[250px]"
              @input="updateFormName"
            />
            <!-- Ticket forms set the category in the Ticket config panel below,
                 so it is not repeated here (it crowded the editable name field). -->
            <span
              v-if="editableForm.form_type === 'contact'"
              class="text-xs text-n-slate-9 bg-n-alpha-2 px-2 py-0.5 rounded shrink-0"
            >
              {{ t('FORM_BUILDER.CONTACT_FORM') }}
            </span>
            <span
              v-if="editableForm.cincel_signature_template_id"
              class="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded shrink-0"
              :title="t('FORM_BUILDER.CINCEL_CONNECTED_HINT')"
            >
              {{ t('FORM_BUILDER.CINCEL_CONNECTED') }}
            </span>
            <div class="flex items-center gap-1.5 ml-2 shrink-0">
              <span class="text-xs text-n-slate-10">
                {{
                  editableForm.active
                    ? t('FORM_BUILDER.ACTIVE')
                    : t('FORM_BUILDER.INACTIVE')
                }}
              </span>
              <button
                class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
                :class="editableForm.active ? 'bg-woot-500' : 'bg-n-slate-7'"
                @click="toggleActive"
              >
                <span
                  class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                  :class="editableForm.active ? 'left-[17px]' : 'left-[2px]'"
                />
              </button>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button
              v-if="editableForm.identifier"
              v-tooltip.top="t('FORM_BUILDER.PREVIEW')"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
              @click="previewForm"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {{ t('FORM_BUILDER.PREVIEW') }}
            </button>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
              @click="deleteForm"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {{ t('FORM_BUILDER.DELETE') }}
            </button>
            <NextButton
              :label="t('FORM_BUILDER.SAVE')"
              color="blue"
              :is-loading="isSaving"
              @click="saveForm"
            />
          </div>
        </div>

        <!-- Pages area -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- Contact identifier warning -->
          <div
            v-if="!hasContactIdentifier && pages.length > 0"
            class="mb-4 px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-start gap-3"
          >
            <svg
              class="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p class="text-sm font-medium text-amber-500">
                {{ t('FORM_BUILDER.CONTACT_REQUIRED.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-10 mt-0.5">
                {{ t('FORM_BUILDER.CONTACT_REQUIRED.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <!-- Ticket: Category / Subcategory (editable, always visible) -->
          <div
            v-if="editableForm.form_type === 'ticket'"
            class="rounded-xl border border-n-weak bg-n-alpha-1 mb-5 p-4"
          >
            <span
              class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.TICKET_CONFIG.TITLE') }}
            </span>
            <div class="grid grid-cols-2 gap-3 mt-2">
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('FORM_BUILDER.TICKET_CONFIG.CATEGORY') }}
                </label>
                <select
                  v-model="editableForm.ticket_category_id"
                  class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                  @change="onCategoryChange"
                >
                  <option
                    v-for="cat in ticketCategories"
                    :key="cat.id"
                    :value="cat.id"
                  >
                    {{ cat.name }} ({{ cat.code }})
                  </option>
                </select>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                >
                  {{ t('FORM_BUILDER.TICKET_CONFIG.SUBCATEGORY') }}
                </label>
                <select
                  v-model="editableForm.default_subcategory_id"
                  :disabled="loadingSubcats"
                  class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 disabled:opacity-50"
                  @change="onSubcategoryChange"
                >
                  <option :value="null">
                    {{ t('FORM_BUILDER.HEADER.NO_SUBCATEGORY') }}
                  </option>
                  <option
                    v-for="sub in subcategories"
                    :key="sub.id"
                    :value="sub.id"
                  >
                    {{ sub.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Form Header Config -->
          <div
            class="rounded-xl border border-woot-500/30 bg-gradient-to-b from-n-blue-2/30 to-transparent mb-5 overflow-hidden"
          >
            <div class="px-4 py-3 flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-lg bg-n-blue-3 flex items-center justify-center shrink-0"
              >
                <svg
                  class="w-4 h-4 text-woot-500"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-0.5">
                  <span
                    class="text-[10px] font-semibold text-woot-500 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.LABEL') }}
                  </span>
                  <span class="text-[10px] text-n-slate-9">
                    {{ t('FORM_BUILDER.HEADER.HINT') }}
                  </span>
                </div>
              </div>
              <button
                class="text-xs text-woot-500 hover:underline flex items-center gap-1"
                @click="toggleFormHeaderConfig"
              >
                <svg
                  class="w-3 h-3 transition-transform"
                  :class="{ 'rotate-180': showFormHeader }"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
                {{ t('FORM_BUILDER.HEADER.CONFIGURE') }}
              </button>
            </div>

            <div v-if="showFormHeader" class="px-4 pb-4">
              <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1 col-span-2">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.FORM_TITLE') }}
                  </label>
                  <input
                    type="text"
                    :value="editableForm.name"
                    class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm font-medium text-n-slate-12"
                    @input="updateFormName"
                  />
                </div>
                <div class="flex flex-col gap-1 col-span-2">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.DESCRIPTION') }}
                  </label>
                  <textarea
                    rows="2"
                    :value="editableForm.description || ''"
                    :placeholder="
                      t('FORM_BUILDER.HEADER.DESCRIPTION_PLACEHOLDER')
                    "
                    class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
                    @input="updateFormDescription"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.URL_SLUG') }}
                  </label>
                  <input
                    type="text"
                    :value="editableForm.slug || ''"
                    class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                    @input="updateFormSlug"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.CONFIRMATION') }}
                  </label>
                  <input
                    type="text"
                    :value="editableForm.confirmation_message || ''"
                    class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                    @input="updateConfirmationMessage"
                  />
                  <span class="text-[10px] text-n-slate-9">
                    {{ t('FORM_BUILDER.HEADER.CONFIRMATION_HINT') }}
                  </span>
                </div>
                <div class="flex flex-col gap-1 col-span-2">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.UPDATE_CONTACT') }}
                  </label>
                  <button
                    class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
                    :class="
                      editableForm.allow_contact_update
                        ? 'bg-woot-500'
                        : 'bg-n-slate-7'
                    "
                    @click="toggleAllowContactUpdate"
                  >
                    <span
                      class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                      :class="
                        editableForm.allow_contact_update
                          ? 'left-[17px]'
                          : 'left-[2px]'
                      "
                    />
                  </button>
                  <span class="text-[10px] text-n-slate-9">
                    {{ t('FORM_BUILDER.HEADER.UPDATE_CONTACT_HINT') }}
                  </span>
                </div>
                <div class="flex flex-col gap-1 col-span-2">
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.CREATES_CONTACT_LABEL') }}
                  </label>
                  <div class="flex items-center gap-3">
                    <button
                      class="w-8 h-[18px] rounded-full relative cursor-pointer border-0 p-0 transition-colors"
                      :class="editableForm.creates_contact ? 'bg-woot-500' : 'bg-n-slate-7'"
                      @click="toggleCreatesContact"
                    >
                      <span
                        class="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-transform"
                        :class="editableForm.creates_contact ? 'left-[17px]' : 'left-[2px]'"
                      />
                    </button>
                    <span class="text-xs text-n-slate-11">
                      {{
                        editableForm.creates_contact
                          ? t('FORM_BUILDER.HEADER.CREATES_CONTACT_ON')
                          : t('FORM_BUILDER.HEADER.CREATES_CONTACT_OFF')
                      }}
                    </span>
                  </div>
                  <span class="text-[10px] text-n-slate-9">
                    {{ t('FORM_BUILDER.HEADER.CREATES_CONTACT_HINT') }}
                  </span>
                </div>
                <div
                  v-if="isContactFocusedForm"
                  class="flex flex-col gap-1 col-span-2"
                >
                  <label
                    class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
                  >
                    {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER') }}
                  </label>
                  <select
                    :value="editableForm.contact_primary_identifier || ''"
                    class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
                    @change="updateContactPrimaryIdentifier"
                  >
                    <option value="">
                      {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER_DEFAULT') }}
                    </option>
                    <option value="email">
                      {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER_EMAIL') }}
                    </option>
                    <option value="phone_number">
                      {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER_PHONE') }}
                    </option>
                    <option value="identifier">
                      {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER_IDENTIFIER') }}
                    </option>
                  </select>
                  <span class="text-[10px] text-n-slate-9">
                    {{ t('FORM_BUILDER.HEADER.PRIMARY_IDENTIFIER_HINT') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Pages -->
          <PageEditor
            v-for="(page, pageIndex) in pages"
            :key="pageIndex"
            :page="page"
            :page-index="pageIndex"
            :total-pages="pages.length"
            :pages="pages"
            :available-fields="availableFields"
            :used-mapped-attributes="usedMappedAttributes"
            @update-page="updated => updatePage(pageIndex, updated)"
            @delete-page="deletePage"
            @update-field="updateField"
            @delete-field="deleteField"
            @drop-field="dropFieldOnPage"
          />

          <!-- Add Page button -->
          <button
            class="w-full py-3 rounded-xl border-2 border-dashed border-n-weak hover:border-n-slate-7 text-n-slate-9 hover:text-n-slate-11 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            @click="addPage"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path d="M12 4v16m8-8H4" />
            </svg>
            {{ t('FORM_BUILDER.ADD_PAGE') }}
          </button>
        </div>
      </div>

      <!-- Right: Field Palette -->
      <FieldPalette
        :form-type="editableForm.form_type"
        :used-mapped-attributes="usedMappedAttributes"
        :bundles="bundles"
        @add-field="addFieldFromPalette"
        @add-fields="addFieldsFromPalette"
      />
    </template>

    <!-- Empty state -->
    <div v-else-if="!isLoading" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <div
          class="w-16 h-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center mx-auto mb-4"
        >
          <svg
            class="w-8 h-8 text-n-slate-9"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            viewBox="0 0 24 24"
          >
            <path
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 class="text-base font-medium text-n-slate-12 mb-1">
          {{ t('FORM_BUILDER.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-10 mb-4">
          {{ t('FORM_BUILDER.EMPTY.DESCRIPTION') }}
        </p>
        <NextButton
          :label="t('FORM_BUILDER.EMPTY.CREATE')"
          color="blue"
          @click="addForm"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-else class="flex-1 flex items-center justify-center">
      <div class="text-sm text-n-slate-10">
        {{ t('FORM_BUILDER.LOADING') }}
      </div>
    </div>

    <!-- New Form Modal -->
    <div
      v-if="showNewFormModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="cancelNewForm"
    >
      <div
        class="bg-n-background rounded-xl border border-n-weak shadow-xl w-full max-w-md overflow-hidden"
      >
        <div class="px-5 py-4 border-b border-n-weak">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('FORM_BUILDER.NEW_FORM_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10 mt-0.5">
            {{ t('FORM_BUILDER.NEW_FORM_MODAL.DESCRIPTION') }}
          </p>
        </div>
        <div class="px-5 py-4 space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('FORM_BUILDER.NEW_FORM_MODAL.NAME_LABEL') }}
            </label>
            <input
              v-model="newFormName"
              type="text"
              :placeholder="t('FORM_BUILDER.NEW_FORM_MODAL.NAME_PLACEHOLDER')"
              class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_LABEL') }}
            </label>
            <select
              v-model="newFormType"
              class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            >
              <option value="ticket">
                {{ t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_TICKET') }}
              </option>
              <option value="contact">
                {{ t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_CONTACT') }}
              </option>
            </select>
            <span class="text-xs text-n-slate-10">
              {{ t('FORM_BUILDER.NEW_FORM_MODAL.TYPE_HINT') }}
            </span>
          </div>
          <div v-if="newFormType === 'ticket'" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('FORM_BUILDER.NEW_FORM_MODAL.CATEGORY_LABEL') }}
            </label>
            <select
              v-model="newFormCategoryId"
              class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
            >
              <option value="" disabled>
                {{ t('FORM_BUILDER.NEW_FORM_MODAL.CATEGORY_PLACEHOLDER') }}
              </option>
              <option
                v-for="cat in ticketCategories"
                :key="cat.id"
                :value="cat.id"
              >
                {{ cat.name }} ({{ cat.code }})
              </option>
            </select>
            <span class="text-xs text-n-slate-10">
              {{ t('FORM_BUILDER.NEW_FORM_MODAL.CATEGORY_HELP') }}
            </span>
          </div>
        </div>
        <div
          class="px-5 py-3 border-t border-n-weak flex items-center justify-end gap-2"
        >
          <button
            class="px-4 py-2 rounded-lg text-sm text-n-slate-11 hover:bg-n-alpha-2"
            @click="cancelNewForm"
          >
            {{ t('FORM_BUILDER.NEW_FORM_MODAL.CANCEL') }}
          </button>
          <NextButton
            :label="t('FORM_BUILDER.NEW_FORM_MODAL.CREATE')"
            color="blue"
            :disabled="
              !newFormName.trim() ||
              (newFormType === 'ticket' && !newFormCategoryId)
            "
            @click="confirmNewForm"
          />
        </div>
      </div>
    </div>
  </div>
</template>
